import { isRecord } from '@Core/utils/recordGuards';
import type { SdkResource } from '@Pages/pageRuntime/sdk/types';
import { sameRecurrenceRule } from '@Scheduling/components/recurrenceRuleModel';
import get from 'lodash/get';
import type { ComponentType } from 'react';

import { RecurringEventableScopePrompt } from './recurringEventableScopePrompt';

/**
 * The value of the reserved `$operation` form key: capability namespaces (e.g.
 * `eventable`) mapping to their operation options. Interactions resolve with this
 * shape and the update form stores the merged result under `$operation` before submit.
 */
export type OperationMetadata = Record<string, Record<string, unknown>>;

export type RecordUpdateInteractionContext = {
  resource: Pick<SdkResource, 'capabilities'>;
  record: Record<string, unknown>;
  /** The payload that the update submits, fixed values included. */
  data: Record<string, unknown>;
};

export type RecordUpdateInteractionPromptProps = {
  resource: Pick<SdkResource, 'singular_name'>;
  context: RecordUpdateInteractionContext;
  isSubmitting: boolean;
  onResolve: (operation: OperationMetadata) => void;
  onCancel: () => void;
};

/**
 * A capability-aware step that runs before an update is submitted. When `appliesTo`
 * matches the loaded record, the turnkey update form renders `Prompt` instead of
 * submitting and waits for the user to resolve it with operation metadata (or cancel).
 *
 * Adding a capability's pre-submit flow is a single registry entry below — every
 * turnkey update form picks it up with no per-page wiring.
 */
export type RecordUpdateInteraction = {
  appliesTo: (context: RecordUpdateInteractionContext) => boolean;
  Prompt: ComponentType<RecordUpdateInteractionPromptProps>;
};

/**
 * Shallow-merges resolved operation namespaces into any `$operation` a custom page
 * already set, so an interaction never clobbers unrelated capability options.
 */
export function mergeOperationMetadata(
  existing: unknown,
  incoming: OperationMetadata
): Record<string, unknown> {
  const merged: Record<string, unknown> = isRecord(existing) ? { ...existing } : {};

  for (const [namespace, options] of Object.entries(incoming)) {
    const current = merged[namespace];
    merged[namespace] = isRecord(current) ? { ...current, ...options } : options;
  }

  return merged;
}

/**
 * Drops the namespaces an interaction resolved, so a rejected submit prompts again
 * on the next attempt while any `$operation` a custom page set stays in place.
 */
export function withoutOperationNamespaces(
  existing: unknown,
  namespaces: readonly string[]
): Record<string, unknown> | undefined {
  if (!isRecord(existing)) {
    return undefined;
  }

  const dropped = new Set(namespaces);
  const remaining = Object.fromEntries(
    Object.entries(existing).filter(([namespace]) => !dropped.has(namespace))
  );

  return Object.keys(remaining).length > 0 ? remaining : undefined;
}

export function isRecurringEventableRecord(
  resource: Pick<SdkResource, 'capabilities'>,
  record: Record<string, unknown>
): boolean {
  const eventScheduleField = resource.capabilities?.eventable?.eventScheduleField;

  if (!eventScheduleField) {
    return false;
  }

  if (isSeriesOccurrence(record)) {
    return true;
  }

  const recurrenceRule = get(record, `${eventScheduleField.key}.recurrence.rule`);

  return typeof recurrenceRule === 'string' && recurrenceRule.trim() !== '';
}

/** An occurrence on the series engine names its series. */
export function isSeriesOccurrence(record: Record<string, unknown>): boolean {
  return get(record, '__meta.capabilities.eventable.series.series_id') != null;
}

/**
 * Whether the submitted form repeats differently from the loaded record. A form without the
 * schedule field leaves the repeat as it is.
 */
export function recurrenceChanged(
  resource: Pick<SdkResource, 'capabilities'>,
  record: Record<string, unknown>,
  data: Record<string, unknown>
): boolean {
  const key = resource.capabilities?.eventable?.eventScheduleField?.key;

  if (!key || get(data, key) === undefined) {
    return false;
  }

  return !sameRecurrenceRule(
    get(record, `${key}.recurrence.rule`),
    get(data, `${key}.recurrence.rule`)
  );
}

export const recurringEventableScopeInteraction: RecordUpdateInteraction = {
  appliesTo: ({ resource, record, data }) =>
    isRecurringEventableRecord(resource, record) && get(data, '$operation.eventable.scope') == null,
  Prompt: RecurringEventableScopePrompt
};

export const recordUpdateInteractions: RecordUpdateInteraction[] = [
  recurringEventableScopeInteraction
];
